import { normalizeNotificationDistance } from "../../../utils/normalizeNotificationDistance";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { Route, RouteFactory } from "../../../../../utils/models/Route";
import { i18n } from "../../../../../i18n";

// Translated when called (never at module load) so route names follow the page's locale.
const routeName = (number: number): string => i18n.global.t('plan_trail.initialization.route_number', { number });

interface ParsedHikerPlannerImport {
  title?: string;
  dayInfo?: { [dayNumber: number]: { name: string; description: string } };
  hikeHighlights?: any[];
  routes: { [key: string]: Route };
}

// Parses the GeoJSON payload stashed in localStorage under 'hikerPlannerImport'
// (e.g. from the "Edit in Hike Planner" button) into routes/day-info/highlights.
// Kept in its own module (rather than initialization.ts, which pulls in
// graphClient/mapboxgl/lodash-es) so it can be unit tested directly. Returns
// null when no routes could be built from the payload.
export function parseHikerPlannerImportData(importedData: any): ParsedHikerPlannerImport | null {
  const routes: { [key: string]: Route } = {};
  let position = 0;
  let title: string | undefined;
  let dayInfo: ParsedHikerPlannerImport['dayInfo'];
  let hikeHighlights: any[] | undefined;

  // Helper function to create start and end markers from lineString coordinates
  const createMarkersFromCoordinates = (coordinates: number[][]): MarkerFeature[] => {
    if (coordinates.length < 2) return [];

    // Only create markers for start and end points.
    // NOTE: these must be real MarkerGeometry/MarkerProperties instances
    // (not plain objects). Otherwise markerGeometry.lon()/.lat() are
    // undefined and markerProperties.isSnap is undefined — which makes
    // extending the route throw inside the snap callback and fall back to
    // a straight, elevation-less line. isSnap:true treats the imported
    // endpoints as network nodes, matching the rest of the planner.
    const startCoord = coordinates[0];
    const endCoord = coordinates[coordinates.length - 1];

    const startMarker = new MarkerFeature(
      new MarkerGeometry([startCoord[0], startCoord[1]]),
      new MarkerProperties(generateID(), true, Date.now().toString(), startCoord[2] ?? 0)
    );

    const endMarker = new MarkerFeature(
      new MarkerGeometry([endCoord[0], endCoord[1]]),
      new MarkerProperties(generateID(), true, Date.now().toString(), endCoord[2] ?? 0)
    );

    return [startMarker, endMarker];
  };

  // Check if it's a FeatureCollection (new format with metadata)
  if (importedData.type === "FeatureCollection" && Array.isArray(importedData.features)) {

    // Load hike name if available
    if (importedData.hikeName) {
      title = importedData.hikeName;
    }

    // Load hikeDays if available (for day names/descriptions)
    if (importedData.hikeDays && Array.isArray(importedData.hikeDays)) {
      dayInfo = {};
      importedData.hikeDays.forEach((day: any) => {
        const dayNumber = day.dayNumber || day.day_number;
        if (dayNumber != null) {
          dayInfo![dayNumber] = {
            name: day.name || '',
            description: day.description || ''
          };
        }
      });
    }

    // Load hike highlights if available
    if (importedData.hikeHighlights && Array.isArray(importedData.hikeHighlights)) {
      hikeHighlights = importedData.hikeHighlights.map((highlight: any) => ({
        clientId: `highlight_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
        type: highlight.type,
        title: highlight.title || '',
        description: highlight.description || '',
        coordinates: highlight.coordinates || [0, 0],
        dayNumber: highlight.dayNumber,
        notificationDistance: normalizeNotificationDistance(highlight.notificationDistance),
        images: (highlight.images || []).map((image: any) => ({
          ...image,
          clientId: image.clientId || `img_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
        })),
        imageSignedIds: undefined,
        imagePreviewUrls: [],
        media: (highlight.media || highlight.mediaAttachments || []).map((item: any) => ({
          ...item,
          clientId: item.clientId || `media_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
          mediaType: item.mediaType || item.media_type || 'audio'
        })),
        mediaSignedIds: highlight.mediaSignedIds || undefined
      }));
    }

    // Group features by day
    const featuresByDay: { [day: number]: any[] } = {};

    importedData.features.forEach((feature: any) => {
      const dayNumber = feature.properties?.hikeDayNumber || 1;
      if (!featuresByDay[dayNumber]) {
        featuresByDay[dayNumber] = [];
      }
      featuresByDay[dayNumber].push(feature);
    });

    // Sort days
    const sortedDays = Object.keys(featuresByDay).map(Number).sort((a, b) => a - b);

    // Create routes for each feature, maintaining day information
    sortedDays.forEach(dayNumber => {
      featuresByDay[dayNumber].forEach((feature: any, indexInDay: number) => {
        const lineStringFeature = LineStringFeature.fromGeoJsonForEdit(feature);
        const markers = createMarkersFromCoordinates(feature.geometry.coordinates);
        const routeStyle = feature.properties?.routeStyle || 'route';
        const name = feature.properties?.name || routeName(position + 1);
        const isReference = feature.properties?.isReference || false;
        const referencedHikes = feature.properties?.referencedHikes || [];
        const ptv2PlannedTrailId = feature.properties?.ptv2PlannedTrailId;

        const route = RouteFactory.createNew(name, position, dayNumber);
        route.markers = markers;
        route.lineStrings = [lineStringFeature];
        route.route_style = routeStyle as 'route' | 'alt' | 'spur' | 'transport';

        // Set reference properties if this is a reference route
        if (isReference && ptv2PlannedTrailId) {
          route.plannedTrailId = Number(ptv2PlannedTrailId);
          route.isReference = true;
          route.referencedHikes = referencedHikes;
        }

        routes[route.id] = route;

        position++;
      });
    });
  }
  // Fallback: Handle old format (LineString or MultiLineString without metadata)
  else if (importedData.type === "LineString") {
    const feature = {
      type: "Feature",
      geometry: importedData,
      properties: {}
    };
    const lineStringFeature = LineStringFeature.fromGeoJsonForEdit(feature);
    const markers = createMarkersFromCoordinates(importedData.coordinates);

    const route = RouteFactory.createNew(routeName(1), position, 1);
    route.markers = markers;
    route.lineStrings = [lineStringFeature];
    routes[route.id] = route;
  } else if (importedData.type === "MultiLineString") {
    for (const coordinates of importedData.coordinates) {
      const feature = {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: coordinates
        },
        properties: {}
      };
      const lineStringFeature = LineStringFeature.fromGeoJsonForEdit(feature);
      const markers = createMarkersFromCoordinates(coordinates);

      const route = RouteFactory.createNew(routeName(position + 1), position, 1);
      route.markers = markers;
      route.lineStrings = [lineStringFeature];
      routes[route.id] = route;
      position++;
    }
  }


  if (Object.keys(routes).length === 0) return null;
  return { title, dayInfo, hikeHighlights, routes };
}
